import { useConnection } from '../../hooks/useConnection';

/**
 * The strip under the nav that says we cannot reach the sale.
 *
 * 🔴 NEUTRAL, NEVER RED — the same locked decision as the phone. An OS-level
 * condition borrowing an auction colour teaches a bidder to read "offline" as
 * "you are losing", and the semantic reds have to keep meaning outbid.
 *
 * RECONNECTING DISABLES NOTHING. Most drops resolve in seconds, and freezing a
 * bidding page over a two-second blip is worse than the blip. Only a sustained
 * drop — see `OFFLINE_AFTER_MS` — freezes the room, at which point every number
 * on screen is a claim we cannot stand behind, so the strip stamps the moment
 * they were last true and the countdowns stop.
 *
 * IT ALSO SAYS WHAT IS STILL WORKING. A bidder offline with a maximum in place
 * has not lost it: the house holds it and it keeps bidding without this page.
 * That sentence is the one that stops someone panicking on a train.
 */
export function ConnectionBar({ houseName }: { houseName: string }) {
  const { state, lastOkAt, retry } = useConnection();
  if (state === 'online') return null;

  const trying = state === 'reconnecting';
  const asOf =
    lastOkAt === null
      ? null
      : new Date(lastOkAt).toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });

  return (
    <div className={trying ? 'wcb wcb-try' : 'wcb'} role="status">
      <div className="wcb-in">
        <b>{trying ? 'Reconnecting' : 'No connection'}</b>
        <span>
          {trying
            ? 'Prices may be a few seconds behind.'
            : `${asOf === null ? 'Prices are not updating' : `Prices as of ${asOf}`}. Maximums you have already set are held by ${houseName} and keep bidding without this page.`}
        </span>
        {trying ? null : (
          <button type="button" className="wcb-b" onClick={retry}>
            Retry
          </button>
        )}
      </div>
    </div>
  );
}
